import { createContext, useContext, useState, useEffect, useRef, useCallback, type ReactNode } from 'react'
import { apiClient } from '../lib/api-client'
import { useQueryClient } from '@tanstack/react-query'

interface AuthUser {
  tenantId: string
  userId: string
  displayName: string
  email: string
  role: string
}

interface AuthContextType {
  user: AuthUser | null
  token: string | null
  isAuthenticated: boolean
  isLoading: boolean
  login: (email: string, password: string) => Promise<void>
  logout: () => void
  getSessionGeneration: () => number
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) throw new Error('useAuth must be used within AuthProvider')
  return context
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient()
  const [user, setUser] = useState<AuthUser | null>(null)
  const [token, setToken] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const sessionGeneration = useRef(0)
  const getSessionGeneration = useCallback(() => sessionGeneration.current, [])

  useEffect(() => {
    const storedToken = sessionStorage.getItem('cot_jwt_token')
    const storedUser = sessionStorage.getItem('cot_user')

    if (storedToken && storedUser) {
      try {
        const parsed = JSON.parse(storedUser)
        setToken(storedToken)
        setUser(parsed)
        apiClient.setToken(storedToken)
      } catch {
        sessionStorage.removeItem('cot_jwt_token')
        sessionStorage.removeItem('cot_user')
      }
    }
    setIsLoading(false)
  }, [])

  const login = async (email: string, password: string) => {
    const generation = ++sessionGeneration.current
    setIsLoading(true)
    try {
      const response = await apiClient.login(email, password)
      if (sessionGeneration.current !== generation) throw new DOMException('Sign-in was canceled. Please sign in again.', 'AbortError')
      if (response.success && response.data) {
        const { token: jwt, tenantId, userId, email, displayName, role } = response.data
        const userData: AuthUser = { tenantId, userId, displayName, email, role }
        if (user && user.userId !== userId) sessionStorage.removeItem('cot_signup_draft')
        queryClient.removeQueries({ queryKey: ['community'] })

        sessionStorage.setItem('cot_jwt_token', jwt)
        sessionStorage.setItem('cot_user', JSON.stringify(userData))
        apiClient.setToken(jwt)
        setToken(jwt)
        setUser(userData)

        await new Promise(resolve => setTimeout(resolve, 0))
      } else {
        throw new Error('Login failed')
      }
    } finally {
      if (sessionGeneration.current === generation) setIsLoading(false)
    }
  }

  const logout = () => {
    sessionGeneration.current += 1
    queryClient.removeQueries({ queryKey: ['community'] })
    sessionStorage.removeItem('cot_signup_draft')
    sessionStorage.removeItem('cot_return_to')
    sessionStorage.removeItem('cot_jwt_token')
    sessionStorage.removeItem('cot_user')
    apiClient.clearToken()
    setUser(null)
    setToken(null)
    setIsLoading(false)
  }

  return (
    <AuthContext.Provider value={{
      user,
      token,
      isAuthenticated: !!token,
      isLoading,
      login,
      logout,
      getSessionGeneration,
    }}>
      {children}
    </AuthContext.Provider>
  )
}
